// src/pages/trading-portal/TradeExecution/OrderManagementPage.js
// Order Management page with 3-tab interface: Active Orders, Order History, Broker Connections

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
    FormControlLabel,
    Checkbox,
    Divider,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    TEEOrderType, TEEOrderTypeLabels, TEEOrderTypeColors,
    OrderSide, OrderSideLabels, OrderSideColors,
    TEEAssetClass, TEEAssetClassLabels,
    TEETimeInForce, TEETimeInForceLabels,
    TEEOrderStatus, TEEOrderStatusLabels, TEEOrderStatusColors,
    BrokerName, BrokerNameLabels, BrokerNameColors,
    ConnectionStatus, ConnectionStatusLabels, ConnectionStatusColors,
    ProtocolType, ProtocolTypeLabels,
    OrderEventType, OrderEventTypeLabels, OrderEventTypeColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const ACTIVE_STATUSES = ['GENERATED', 'VALIDATED', 'SUBMITTED', 'ACKNOWLEDGED', 'PARTIAL_FILL', 'PENDING_CANCEL'];
const HISTORY_STATUSES = ['FILLED', 'CANCELLED', 'REJECTED', 'EXPIRED', 'SETTLED'];

const NEEDS_LIMIT_PRICE = ['LIMIT', 'STOP_LIMIT', 'LOC'];
const NEEDS_STOP_PRICE = ['STOP', 'STOP_LIMIT'];
const NEEDS_TRAILING = ['TRAILING_STOP'];

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const OrderManagementPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ==================== TAB 0: ACTIVE ORDERS ====================
    const [activeOrders, setActiveOrders] = useState([]);
    const [activeLoading, setActiveLoading] = useState(true);
    const [activePage, setActivePage] = useState(0);
    const [activePerPage, setActivePerPage] = useState(10);
    const [activeTotal, setActiveTotal] = useState(0);
    const [activeStatusFilter, setActiveStatusFilter] = useState('');
    const [activeSideFilter, setActiveSideFilter] = useState('');
    const [activeSymbolSearch, setActiveSymbolSearch] = useState('');

    // ==================== TAB 1: ORDER HISTORY ====================
    const [historyOrders, setHistoryOrders] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyPerPage, setHistoryPerPage] = useState(10);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historySymbolSearch, setHistorySymbolSearch] = useState('');
    const [historyDateFrom, setHistoryDateFrom] = useState('');
    const [historyDateTo, setHistoryDateTo] = useState('');

    // ==================== TAB 2: BROKER CONNECTIONS ====================
    const [brokerConnections, setBrokerConnections] = useState([]);
    const [brokersLoading, setBrokersLoading] = useState(false);
    const [testingConnectionId, setTestingConnectionId] = useState(null);

    // ==================== DIALOGS ====================
    // New Order Dialog
    const [orderDialog, setOrderDialog] = useState(false);
    const [orderForm, setOrderForm] = useState({
        symbol: '',
        symbol_id: null,
        order_side: '',
        order_type: '',
        asset_class: '',
        time_in_force: 'DAY',
        quantity: '',
        limit_price: '',
        stop_price: '',
        trailing_amount: '',
        trailing_percent: '',
        broker_connection_id: '',
        notes: '',
    });
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);
    const [symbolsLoading, setSymbolsLoading] = useState(false);
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [indicatorData, setIndicatorData] = useState(null);
    const [indicatorLoading, setIndicatorLoading] = useState(false);

    // Order Detail Dialog
    const [detailDialog, setDetailDialog] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Broker Connection Dialog (Add/Edit)
    const [brokerDialog, setBrokerDialog] = useState(false);
    const [brokerEditing, setBrokerEditing] = useState(null);
    const [brokerForm, setBrokerForm] = useState({
        broker_name: '',
        connection_name: '',
        protocol_type: '',
        api_endpoint: '',
        is_paper_trading: false,
        credentials_api_key: '',
        credentials_api_secret: '',
    });

    // Delete Confirmation Dialog
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Cancel Order Confirmation Dialog
    const [cancelDialog, setCancelDialog] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);

    // Amend Order Dialog
    const [amendDialog, setAmendDialog] = useState(false);
    const [amendTarget, setAmendTarget] = useState(null);
    const [amendForm, setAmendForm] = useState({
        quantity: '',
        limit_price: '',
        stop_price: '',
        trailing_amount: '',
        trailing_percent: '',
    });

    // Broker connections list for order form dropdown
    const [brokerConnectionOptions, setBrokerConnectionOptions] = useState([]);

    // ==================== FORMAT HELPERS ====================
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-tee-orders', 'view'),
                hasPermission('tr-tee-orders', 'add'),
                hasPermission('tr-tee-orders', 'edit'),
                hasPermission('tr-tee-orders', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
        fetchBrokerConnectionOptions();
    }, []);

    const fetchBrokerConnectionOptions = async () => {
        try {
            const res = await axiosInstance.get('/api/trade-execution/broker-connections');
            if (res.data.success) {
                setBrokerConnectionOptions(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch broker connections for dropdown:', err);
        }
    };

    // ==================== FETCH WATCHLIST SYMBOLS ====================
    const fetchWatchlistSymbols = useCallback(async () => {
        setSymbolsLoading(true);
        try {
            const params = new URLSearchParams({ page: 1, per_page: 500, status: 'ACTIVE' });
            const response = await axiosInstance.get(`/api/stock-trading/symbols?${params.toString()}`);
            if (response.data.success) {
                setWatchlistSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch watchlist symbols:', err);
        } finally {
            setSymbolsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchWatchlistSymbols();
    }, [canView, fetchWatchlistSymbols]);

    // ==================== TAB 0: ACTIVE ORDERS ====================
    const fetchActiveOrders = useCallback(async () => {
        if (!canView) return;
        setActiveLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: activePage + 1, per_page: activePerPage });
            if (activeStatusFilter) {
                params.append('status', activeStatusFilter);
            } else {
                params.append('status', ACTIVE_STATUSES.join(','));
            }
            if (activeSideFilter) params.append('order_side', activeSideFilter);
            if (activeSymbolSearch) params.append('symbol', activeSymbolSearch);
            const res = await axiosInstance.get(`/api/trade-execution/orders?${params.toString()}`);
            if (res.data.success) {
                setActiveOrders(res.data.data || []);
                setActiveTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch active orders');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch active orders');
        } finally {
            setActiveLoading(false);
        }
    }, [canView, activePage, activePerPage, activeStatusFilter, activeSideFilter, activeSymbolSearch]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchActiveOrders();
    }, [fetchActiveOrders, canView, activeTab]);

    // Auto-refresh polling for active orders tab (every 15 seconds)
    useEffect(() => {
        if (!canView || activeTab !== 0) return;
        const interval = setInterval(() => {
            fetchActiveOrders();
        }, 15000);
        return () => clearInterval(interval);
    }, [canView, activeTab, fetchActiveOrders]);

    // ==================== TAB 1: ORDER HISTORY ====================
    const fetchHistoryOrders = useCallback(async () => {
        if (!canView) return;
        setHistoryLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: historyPage + 1,
                per_page: historyPerPage,
                status: HISTORY_STATUSES.join(','),
            });
            if (historySymbolSearch) params.append('symbol', historySymbolSearch);
            if (historyDateFrom) params.append('date_from', historyDateFrom);
            if (historyDateTo) params.append('date_to', historyDateTo);
            const res = await axiosInstance.get(`/api/trade-execution/orders?${params.toString()}`);
            if (res.data.success) {
                setHistoryOrders(res.data.data || []);
                setHistoryTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch order history');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch order history');
        } finally {
            setHistoryLoading(false);
        }
    }, [canView, historyPage, historyPerPage, historySymbolSearch, historyDateFrom, historyDateTo]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchHistoryOrders();
    }, [fetchHistoryOrders, canView, activeTab]);

    // ==================== TAB 2: BROKER CONNECTIONS ====================
    const fetchBrokerConnections = useCallback(async () => {
        if (!canView) return;
        setBrokersLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/trade-execution/broker-connections');
            if (res.data.success) {
                setBrokerConnections(res.data.data || []);
            } else {
                setError(res.data.message || 'Failed to fetch broker connections');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch broker connections');
        } finally {
            setBrokersLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchBrokerConnections();
    }, [fetchBrokerConnections, canView, activeTab]);

    // ==================== ORDER ACTIONS ====================
    const handleOpenNewOrder = () => {
        setOrderForm({
            symbol: '',
            order_side: '',
            order_type: '',
            asset_class: 'EQUITY',
            time_in_force: 'DAY',
            quantity: '',
            limit_price: '',
            stop_price: '',
            trailing_amount: '',
            trailing_percent: '',
            broker_connection_id: '',
            notes: '',
        });
        setOrderDialog(true);
    };

    const handleOrderFormChange = (field, value) => {
        setOrderForm((prev) => ({ ...prev, [field]: value }));
    };

    const validateOrderForm = (form) => {
        if (NEEDS_LIMIT_PRICE.includes(form.order_type)) {
            const lp = parseFloat(form.limit_price);
            if (!form.limit_price || isNaN(lp) || lp <= 0) {
                return 'Limit price is required and must be positive for this order type.';
            }
        }
        if (NEEDS_STOP_PRICE.includes(form.order_type)) {
            const sp = parseFloat(form.stop_price);
            if (!form.stop_price || isNaN(sp) || sp <= 0) {
                return 'Stop price is required and must be positive for this order type.';
            }
        }
        if (NEEDS_TRAILING.includes(form.order_type)) {
            const ta = parseFloat(form.trailing_amount);
            const tp = parseFloat(form.trailing_percent);
            const hasAmount = form.trailing_amount && !isNaN(ta) && ta > 0;
            const hasPercent = form.trailing_percent && !isNaN(tp) && tp > 0;
            if (!hasAmount && !hasPercent) {
                return 'Trailing amount or trailing percent is required for trailing stop orders.';
            }
        }
        return null;
    };

    const isOrderFormValid = () => {
        if (!orderForm.symbol || !orderForm.order_side || !orderForm.order_type || !orderForm.quantity) return false;
        return !validateOrderForm(orderForm);
    };

    const handleSubmitOrder = async () => {
        const validationError = validateOrderForm(orderForm);
        if (validationError) {
            setError(validationError);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                symbol: orderForm.symbol,
                symbol_id: orderForm.symbol_id,
                order_side: orderForm.order_side,
                order_type: orderForm.order_type,
                asset_class: orderForm.asset_class,
                time_in_force: orderForm.time_in_force,
                quantity: orderForm.quantity ? parseInt(orderForm.quantity) : null,
                notes: orderForm.notes || null,
            };
            if (orderForm.broker_connection_id) {
                payload.broker_connection_id = parseInt(orderForm.broker_connection_id);
            }
            if (NEEDS_LIMIT_PRICE.includes(orderForm.order_type) && orderForm.limit_price) {
                payload.limit_price = parseFloat(orderForm.limit_price);
            }
            if (NEEDS_STOP_PRICE.includes(orderForm.order_type) && orderForm.stop_price) {
                payload.stop_price = parseFloat(orderForm.stop_price);
            }
            if (NEEDS_TRAILING.includes(orderForm.order_type)) {
                if (orderForm.trailing_amount) payload.trailing_amount = parseFloat(orderForm.trailing_amount);
                if (orderForm.trailing_percent) payload.trailing_percent = parseFloat(orderForm.trailing_percent);
            }
            const res = await axiosInstance.post('/api/trade-execution/orders', payload);
            if (res.data.success) {
                setOrderDialog(false);
                setSelectedSymbol(null);
                setIndicatorData(null);
                setSnackbar({ open: true, message: 'Order created successfully', severity: 'success' });
                fetchActiveOrders();
            } else {
                setError(res.data.message || 'Failed to create order');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create order');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitOrderToMarket = async (orderId) => {
        try {
            const res = await axiosInstance.post(`/api/trade-execution/orders/${orderId}/submit`);
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Order submitted to market', severity: 'success' });
                fetchActiveOrders();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to submit order', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to submit order', severity: 'error' });
        }
    };

    const handleSyncOrder = async (orderId) => {
        try {
            const res = await axiosInstance.post(`/api/trade-execution/orders/${orderId}/sync`);
            if (res.data.success) {
                setSnackbar({ open: true, message: res.data.message || 'Order synced', severity: 'success' });
                fetchActiveOrders();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Sync failed', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to sync order', severity: 'error' });
        }
    };

    const handleCancelOrder = async () => {
        if (!cancelTarget) return;
        try {
            const res = await axiosInstance.post(`/api/trade-execution/orders/${cancelTarget.id}/cancel`);
            if (res.data.success) {
                setCancelDialog(false);
                setCancelTarget(null);
                setSnackbar({ open: true, message: 'Order cancelled', severity: 'success' });
                fetchActiveOrders();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to cancel order', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to cancel order', severity: 'error' });
        }
    };

    const handleOpenAmendDialog = (order) => {
        setAmendTarget(order);
        setAmendForm({
            quantity: order.quantity != null ? String(order.quantity) : '',
            limit_price: order.limit_price != null ? String(order.limit_price) : '',
            stop_price: order.stop_price != null ? String(order.stop_price) : '',
            trailing_amount: order.trailing_amount != null ? String(order.trailing_amount) : '',
            trailing_percent: order.trailing_percent != null ? String(order.trailing_percent) : '',
        });
        setAmendDialog(true);
    };

    const handleAmendFormChange = (field, value) => {
        setAmendForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmitAmend = async () => {
        if (!amendTarget) return;
        setSubmitting(true);
        setError(null);
        try {
            const payload = {};
            if (amendForm.quantity) payload.quantity = parseInt(amendForm.quantity);
            if (amendForm.limit_price) payload.limit_price = parseFloat(amendForm.limit_price);
            if (amendForm.stop_price) payload.stop_price = parseFloat(amendForm.stop_price);
            if (amendForm.trailing_amount) payload.trailing_amount = parseFloat(amendForm.trailing_amount);
            if (amendForm.trailing_percent) payload.trailing_percent = parseFloat(amendForm.trailing_percent);
            const res = await axiosInstance.put(`/api/trade-execution/orders/${amendTarget.id}`, payload);
            if (res.data.success) {
                setAmendDialog(false);
                setAmendTarget(null);
                setSnackbar({ open: true, message: 'Order amended successfully', severity: 'success' });
                fetchActiveOrders();
            } else {
                setError(res.data.message || 'Failed to amend order');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to amend order');
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewOrderDetail = async (orderId) => {
        setDetailDialog(true);
        setDetailLoading(true);
        setDetailData(null);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/orders/${orderId}?include_fills=true&include_audit=true`);
            if (res.data.success) {
                setDetailData(res.data.data);
            } else {
                setError(res.data.message || 'Failed to fetch order detail');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch order detail');
        } finally {
            setDetailLoading(false);
        }
    };

    const isSubmittable = (status) => ['GENERATED', 'VALIDATED'].includes(status);
    const isCancellable = (status) => ['GENERATED', 'VALIDATED', 'SUBMITTED', 'ACKNOWLEDGED', 'PARTIAL_FILL'].includes(status);

    // ==================== BROKER CONNECTION ACTIONS ====================
    const handleOpenBrokerDialog = (row = null) => {
        if (row) {
            setBrokerEditing(row);
            setBrokerForm({
                broker_name: row.broker_name || '',
                connection_name: row.connection_name || '',
                protocol_type: row.protocol_type || '',
                api_endpoint: row.api_endpoint || '',
                is_paper_trading: row.is_paper_trading || false,
            });
        } else {
            setBrokerEditing(null);
            setBrokerForm({
                broker_name: '',
                connection_name: '',
                protocol_type: '',
                api_endpoint: '',
                is_paper_trading: false,
            });
        }
        setBrokerDialog(true);
    };

    const handleSaveBrokerConnection = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                broker_name: brokerForm.broker_name,
                connection_name: brokerForm.connection_name,
                protocol_type: brokerForm.protocol_type,
                api_endpoint: brokerForm.api_endpoint,
                is_paper_trading: brokerForm.is_paper_trading,
            };
            if (brokerForm.credentials_api_key || brokerForm.credentials_api_secret) {
                payload.credentials_encrypted = {
                    api_key: brokerForm.credentials_api_key,
                    api_secret: brokerForm.credentials_api_secret,
                };
            }
            let res;
            if (brokerEditing) {
                res = await axiosInstance.put(`/api/trade-execution/broker-connections/${brokerEditing.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/trade-execution/broker-connections', payload);
            }
            if (res.data.success) {
                setBrokerDialog(false);
                setSnackbar({ open: true, message: `Broker connection ${brokerEditing ? 'updated' : 'created'} successfully`, severity: 'success' });
                fetchBrokerConnections();
                fetchBrokerConnectionOptions();
            } else {
                setError(res.data.message || 'Failed to save broker connection');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save broker connection');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteBrokerConnection = async () => {
        if (!deleteTarget) return;
        try {
            const res = await axiosInstance.delete(`/api/trade-execution/broker-connections/${deleteTarget.id}`);
            if (res.data.success) {
                setDeleteDialog(false);
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Broker connection deleted', severity: 'success' });
                fetchBrokerConnections();
                fetchBrokerConnectionOptions();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to delete connection', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete connection', severity: 'error' });
        }
    };

    const handleTestConnection = async (connectionId) => {
        setTestingConnectionId(connectionId);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/broker-connections/${connectionId}/test`);
            if (res.data.success) {
                const accountInfo = res.data.data?.account_info;
                const msg = accountInfo
                    ? `Connected! Buying Power: $${parseFloat(accountInfo.buying_power).toLocaleString()}, Equity: $${parseFloat(accountInfo.equity).toLocaleString()}`
                    : 'Connection test successful';
                setSnackbar({ open: true, message: msg, severity: 'success' });
                fetchBrokerConnections();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Connection test failed', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Connection test failed', severity: 'error' });
        } finally {
            setTestingConnectionId(null);
        }
    };

    // ==================== RENDER: LOADING SKELETON ====================
    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    // ==================== RENDER: ORDERS TABLE (shared between Tab 0 & 1) ====================
    const renderOrdersTable = (orders, loading, page, perPage, total, onPageChange, onPerPageChange, isHistory = false) => (
        <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
            {loading ? renderSkeleton() : (
                <>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Symbol</TableCell>
                                    <TableCell sx={headCellSx}>Side</TableCell>
                                    <TableCell sx={headCellSx}>Type</TableCell>
                                    <TableCell sx={headCellSx}>Qty</TableCell>
                                    <TableCell sx={headCellSx}>Filled</TableCell>
                                    <TableCell sx={headCellSx}>Limit Price</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                    <TableCell sx={headCellSx}>Submitted</TableCell>
                                    <TableCell sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.Receipt sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                {isHistory ? 'No order history found' : 'No active orders'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : orders.map((order) => (
                                    <TableRow key={order.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {order.symbol || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={OrderSideLabels[order.order_side] || order.order_side}
                                                size="small"
                                                color={OrderSideColors[order.order_side] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={TEEOrderTypeLabels[order.order_type] || order.order_type}
                                                size="small"
                                                color={TEEOrderTypeColors[order.order_type] || 'default'}
                                                variant="outlined"
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatNumber(order.quantity)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatNumber(order.filled_quantity != null ? order.filled_quantity : order.filled_qty)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {formatPrice(order.limit_price)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={TEEOrderStatusLabels[order.status] || order.status}
                                                size="small"
                                                color={TEEOrderStatusColors[order.status] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                            {formatDateTime(order.submitted_at || order.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="View Detail">
                                                    <IconButton size="small" onClick={() => handleViewOrderDetail(order.id)}>
                                                        <MuiIcons.Visibility sx={{ fontSize: 18, color: '#64748b' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {!isHistory && isSubmittable(order.status) && canEdit && (
                                                    <Tooltip title="Submit to Market">
                                                        <IconButton size="small" onClick={() => handleSubmitOrderToMarket(order.id)}>
                                                            <MuiIcons.Send sx={{ fontSize: 18, color: ACCENT }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {!isHistory && order.status === 'GENERATED' && canEdit && (
                                                    <Tooltip title="Amend Order">
                                                        <IconButton size="small" onClick={() => handleOpenAmendDialog(order)}>
                                                            <MuiIcons.EditNote sx={{ fontSize: 18, color: '#3b82f6' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {!isHistory && isCancellable(order.status) && canEdit && (
                                                    <Tooltip title="Cancel Order">
                                                        <IconButton size="small" onClick={() => { setCancelTarget(order); setCancelDialog(true); }}>
                                                            <MuiIcons.Cancel sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {!isHistory && ['SUBMITTED', 'ACKNOWLEDGED', 'PARTIAL_FILL'].includes(order.status) && (
                                                    <Tooltip title="Sync from Broker">
                                                        <IconButton size="small" onClick={() => handleSyncOrder(order.id)}>
                                                            <MuiIcons.Sync sx={{ fontSize: 18, color: '#3b82f6' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => onPageChange(newPage)}
                        rowsPerPage={perPage}
                        onRowsPerPageChange={(e) => { onPerPageChange(parseInt(e.target.value, 10)); onPageChange(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                    />
                </>
            )}
        </Paper>
    );

    // ==================== RENDER: TAB 0 - ACTIVE ORDERS ====================
    const renderActiveOrders = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                        Active Orders
                    </Typography>
                    <Chip
                        icon={<MuiIcons.AutorenewOutlined sx={{ fontSize: 14 }} />}
                        label="Auto-refresh: ON"
                        size="small"
                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24, bgcolor: '#dcfce7', color: '#16a34a' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchActiveOrders}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={handleOpenNewOrder}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            New Order
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontFamily }}>Status</InputLabel>
                    <Select
                        value={activeStatusFilter}
                        onChange={(e) => { setActiveStatusFilter(e.target.value); setActivePage(0); }}
                        label="Status"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All Active</MenuItem>
                        {ACTIVE_STATUSES.map((s) => (
                            <MenuItem key={s} value={s} sx={{ fontFamily }}>{TEEOrderStatusLabels[s]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ fontFamily }}>Side</InputLabel>
                    <Select
                        value={activeSideFilter}
                        onChange={(e) => { setActiveSideFilter(e.target.value); setActivePage(0); }}
                        label="Side"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All Sides</MenuItem>
                        {Object.entries(OrderSide).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{OrderSideLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    placeholder="Search symbol..."
                    value={activeSymbolSearch}
                    onChange={(e) => { setActiveSymbolSearch(e.target.value); setActivePage(0); }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                    InputProps={{
                        startAdornment: <MuiIcons.Search sx={{ fontSize: 18, color: '#94a3b8', mr: 0.5 }} />,
                    }}
                />
            </Box>

            {renderOrdersTable(
                activeOrders,
                activeLoading,
                activePage,
                activePerPage,
                activeTotal,
                setActivePage,
                setActivePerPage,
                false
            )}
        </Box>
    );

    // ==================== RENDER: TAB 1 - ORDER HISTORY ====================
    const renderOrderHistory = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Order History
                </Typography>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={fetchHistoryOrders}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search symbol..."
                    value={historySymbolSearch}
                    onChange={(e) => { setHistorySymbolSearch(e.target.value); setHistoryPage(0); }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                    InputProps={{
                        startAdornment: <MuiIcons.Search sx={{ fontSize: 18, color: '#94a3b8', mr: 0.5 }} />,
                    }}
                />
                <TextField
                    size="small"
                    type="date"
                    label="From"
                    value={historyDateFrom}
                    onChange={(e) => { setHistoryDateFrom(e.target.value); setHistoryPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                />
                <TextField
                    size="small"
                    type="date"
                    label="To"
                    value={historyDateTo}
                    onChange={(e) => { setHistoryDateTo(e.target.value); setHistoryPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                />
            </Box>

            {renderOrdersTable(
                historyOrders,
                historyLoading,
                historyPage,
                historyPerPage,
                historyTotal,
                setHistoryPage,
                setHistoryPerPage,
                true
            )}
        </Box>
    );

    // ==================== RENDER: TAB 2 - BROKER CONNECTIONS ====================
    const renderBrokerConnections = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Broker Connections
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchBrokerConnections}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={() => handleOpenBrokerDialog()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Add Connection
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {brokersLoading ? renderSkeleton() : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={headCellSx}>Broker Name</TableCell>
                                    <TableCell sx={headCellSx}>Connection Name</TableCell>
                                    <TableCell sx={headCellSx}>Protocol</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                    <TableCell sx={headCellSx}>Paper Trading</TableCell>
                                    <TableCell sx={headCellSx}>Last Connected</TableCell>
                                    <TableCell sx={headCellSx}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {brokerConnections.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <MuiIcons.Cable sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No broker connections configured</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : brokerConnections.map((conn) => (
                                    <TableRow key={conn.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                        <TableCell>
                                            <Chip
                                                label={BrokerNameLabels[conn.broker_name] || conn.broker_name}
                                                size="small"
                                                color={BrokerNameColors[conn.broker_name] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                            {conn.connection_name || '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                            {ProtocolTypeLabels[conn.protocol_type] || conn.protocol_type || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ConnectionStatusLabels[conn.connection_status] || conn.connection_status || 'Unknown'}
                                                size="small"
                                                color={ConnectionStatusColors[conn.connection_status] || 'default'}
                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                            {conn.is_paper_trading ? (
                                                <Chip label="Paper" size="small" color="warning" variant="outlined" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                                            ) : (
                                                <Chip label="Live" size="small" color="success" variant="outlined" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                            {formatDateTime(conn.last_connected_at)}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenBrokerDialog(conn)}>
                                                            <MuiIcons.Edit sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => { setDeleteTarget(conn); setDeleteDialog(true); }}>
                                                            <MuiIcons.Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Test Connection">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleTestConnection(conn.id)}
                                                        disabled={testingConnectionId === conn.id}
                                                    >
                                                        {testingConnectionId === conn.id ? (
                                                            <MuiIcons.HourglassEmpty sx={{ fontSize: 18, color: '#94a3b8' }} />
                                                        ) : (
                                                            <MuiIcons.NetworkCheck sx={{ fontSize: 18, color: ACCENT }} />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: NEW ORDER DIALOG ====================
    const renderOrderDialog = () => (
        <Dialog open={orderDialog} onClose={() => setOrderDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5 }}>
                New Order
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            size="small"
                            options={watchlistSymbols}
                            loading={symbolsLoading}
                            value={selectedSymbol}
                            onChange={(e, newValue) => {
                                setSelectedSymbol(newValue);
                                if (newValue) {
                                    handleOrderFormChange('symbol', newValue.symbol);
                                    handleOrderFormChange('symbol_id', newValue.id);
                                    // Fetch indicators for selected symbol
                                    setIndicatorLoading(true);
                                    setIndicatorData(null);
                                    axiosInstance.get(`/api/stock-trading/symbols/${newValue.id}/quick-indicators`)
                                        .then((res) => { if (res.data.success) setIndicatorData(res.data.data); })
                                        .catch(() => {})
                                        .finally(() => setIndicatorLoading(false));
                                } else {
                                    handleOrderFormChange('symbol', '');
                                    handleOrderFormChange('symbol_id', null);
                                    setIndicatorData(null);
                                }
                            }}
                            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ fontFamily, fontSize: '0.85rem' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>{option.symbol}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{option.company_name}</Typography>
                                        </Box>
                                        {option.current_price && (
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#1e293b' }}>
                                                ${option.current_price?.toFixed(2)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Symbol"
                                    placeholder="Search symbols from watchlist..."
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {symbolsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    {/* ── Indicator Panel ── */}
                    {(indicatorLoading || indicatorData) && (
                        <Grid item xs={12}>
                            <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5 }}>
                                {indicatorLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b' }}>Loading indicators...</Typography>
                                    </Box>
                                ) : indicatorData && indicatorData.indicators?.length > 0 ? (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Technical Indicators
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Chip label={`${indicatorData.summary?.buy || 0} Buy`} size="small" color="success" sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                                                <Chip label={`${indicatorData.summary?.sell || 0} Sell`} size="small" color="error" sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                                                <Chip label={`${indicatorData.summary?.neutral || 0} Neutral`} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', height: 20, bgcolor: '#e2e8f0', color: '#64748b' }} />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                            {indicatorData.indicators.map((ind, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, px: 1, py: 0.4 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>{ind.label}</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 700, color: '#1e293b' }}>
                                                        {ind.value != null ? (ind.type === 'RSI' || ind.type === 'CCI' || ind.type === 'WILLIAMS_R' ? ind.value.toFixed(1) : `$${ind.value.toFixed(2)}`) : '-'}
                                                    </Typography>
                                                    <Chip
                                                        label={ind.signal}
                                                        size="small"
                                                        color={ind.signal === 'BUY' ? 'success' : ind.signal === 'SELL' ? 'error' : 'default'}
                                                        sx={{ fontFamily, fontWeight: 700, fontSize: '0.6rem', height: 18, minWidth: 36 }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                ) : indicatorData?.message ? (
                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8' }}>{indicatorData.message}</Typography>
                                ) : null}
                            </Box>
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Order Side</InputLabel>
                            <Select
                                value={orderForm.order_side}
                                onChange={(e) => handleOrderFormChange('order_side', e.target.value)}
                                label="Order Side"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(OrderSide).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{OrderSideLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Order Type</InputLabel>
                            <Select
                                value={orderForm.order_type}
                                onChange={(e) => handleOrderFormChange('order_type', e.target.value)}
                                label="Order Type"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(TEEOrderType).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{TEEOrderTypeLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Asset Class</InputLabel>
                            <Select
                                value={orderForm.asset_class}
                                onChange={(e) => handleOrderFormChange('asset_class', e.target.value)}
                                label="Asset Class"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(TEEAssetClass).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{TEEAssetClassLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Time in Force</InputLabel>
                            <Select
                                value={orderForm.time_in_force}
                                onChange={(e) => handleOrderFormChange('time_in_force', e.target.value)}
                                label="Time in Force"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(TEETimeInForce).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{TEETimeInForceLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Quantity"
                            type="number"
                            value={orderForm.quantity}
                            onChange={(e) => handleOrderFormChange('quantity', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    {NEEDS_LIMIT_PRICE.includes(orderForm.order_type) && (
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Limit Price"
                                type="number"
                                value={orderForm.limit_price}
                                onChange={(e) => handleOrderFormChange('limit_price', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 0, step: '0.01' }}
                            />
                        </Grid>
                    )}
                    {NEEDS_STOP_PRICE.includes(orderForm.order_type) && (
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Stop Price"
                                type="number"
                                value={orderForm.stop_price}
                                onChange={(e) => handleOrderFormChange('stop_price', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 0, step: '0.01' }}
                            />
                        </Grid>
                    )}
                    {NEEDS_TRAILING.includes(orderForm.order_type) && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Trailing Amount ($)"
                                    type="number"
                                    value={orderForm.trailing_amount}
                                    onChange={(e) => handleOrderFormChange('trailing_amount', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Trailing Percent (%)"
                                    type="number"
                                    value={orderForm.trailing_percent}
                                    onChange={(e) => handleOrderFormChange('trailing_percent', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                    inputProps={{ min: 0, step: '0.1' }}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Broker Connection</InputLabel>
                            <Select
                                value={orderForm.broker_connection_id}
                                onChange={(e) => handleOrderFormChange('broker_connection_id', e.target.value)}
                                label="Broker Connection"
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="" sx={{ fontFamily }}>None</MenuItem>
                                {brokerConnectionOptions.map((bc) => (
                                    <MenuItem key={bc.id} value={bc.id} sx={{ fontFamily }}>
                                        {bc.connection_name} ({BrokerNameLabels[bc.broker_name] || bc.broker_name})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Notes"
                            value={orderForm.notes}
                            onChange={(e) => handleOrderFormChange('notes', e.target.value)}
                            multiline
                            rows={2}
                            sx={{ fontFamily, '& textarea': { fontFamily } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={() => setOrderDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmitOrder}
                    variant="contained"
                    disabled={submitting || !isOrderFormValid()}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Creating...' : 'Create Order'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: ORDER DETAIL DIALOG ====================
    const renderDetailDialog = () => {
        const order = detailData;
        const fills = order?.fills || [];
        const audit = order?.audit_trail || order?.events || [];

        return (
            <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MuiIcons.Receipt sx={{ color: ACCENT }} />
                        Order Detail {order ? `#${order.id}` : ''}
                    </Box>
                    {order && (
                        <Chip
                            label={TEEOrderStatusLabels[order.status] || order.status}
                            size="small"
                            color={TEEOrderStatusColors[order.status] || 'default'}
                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem' }}
                        />
                    )}
                </DialogTitle>
                <DialogContent sx={{ pt: '16px !important' }}>
                    {detailLoading ? (
                        renderSkeleton(8)
                    ) : !order ? (
                        <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 4 }}>No data available</Typography>
                    ) : (
                        <>
                            {/* Order Info Grid */}
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 1.5 }}>
                                Order Information
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Symbol</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{order.symbol || '-'}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Side</Typography>
                                    <Chip label={OrderSideLabels[order.order_side] || order.order_side} size="small" color={OrderSideColors[order.order_side] || 'default'} sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</Typography>
                                    <Chip label={TEEOrderTypeLabels[order.order_type] || order.order_type} size="small" color={TEEOrderTypeColors[order.order_type] || 'default'} variant="outlined" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }} />
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quantity</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatNumber(order.quantity)}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filled</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatNumber(order.filled_quantity != null ? order.filled_quantity : order.filled_qty)}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Asset Class</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{TEEAssetClassLabels[order.asset_class] || order.asset_class || '-'}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time in Force</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{TEETimeInForceLabels[order.time_in_force] || order.time_in_force || '-'}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Limit Price</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatPrice(order.limit_price)}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stop Price</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatPrice(order.stop_price)}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Fill Price</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatPrice(order.avg_fill_price || order.average_fill_price)}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Broker</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{order.broker_connection_name || BrokerNameLabels[order.broker_name] || '-'}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submitted</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>{formatDateTime(order.submitted_at || order.created_at)}</Typography>
                                </Grid>
                                {order.notes && (
                                    <Grid item xs={12}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</Typography>
                                        <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.85rem', mt: 0.5 }}>{order.notes}</Typography>
                                    </Grid>
                                )}
                            </Grid>

                            {/* Fills Table */}
                            <Divider sx={{ mb: 2 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 1.5 }}>
                                Fills ({fills.length})
                            </Typography>
                            {fills.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.82rem' }}>No fills yet</Typography>
                                </Box>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', mb: 3 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                <TableCell sx={headCellSx}>Fill ID</TableCell>
                                                <TableCell sx={headCellSx}>Quantity</TableCell>
                                                <TableCell sx={headCellSx}>Price</TableCell>
                                                <TableCell sx={headCellSx}>Commission</TableCell>
                                                <TableCell sx={headCellSx}>Venue</TableCell>
                                                <TableCell sx={headCellSx}>Time</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {fills.map((fill, idx) => (
                                                <TableRow key={fill.id || idx} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                                    <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>{fill.id || fill.fill_id || idx + 1}</TableCell>
                                                    <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>{formatNumber(fill.quantity || fill.fill_quantity)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>{formatPrice(fill.price || fill.fill_price)}</TableCell>
                                                    <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>{formatPrice(fill.commission)}</TableCell>
                                                    <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>{fill.venue || fill.execution_venue || '-'}</TableCell>
                                                    <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>{formatDateTime(fill.filled_at || fill.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {/* Audit Trail */}
                            <Divider sx={{ mb: 2 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 1.5 }}>
                                Audit Trail ({audit.length})
                            </Typography>
                            {audit.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.82rem' }}>No audit events</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ pl: 1 }}>
                                    {audit.map((event, idx) => (
                                        <Box key={event.id || idx} sx={{ display: 'flex', gap: 2, mb: 1.5, position: 'relative', pl: 3 }}>
                                            {/* Timeline dot and line */}
                                            <Box sx={{ position: 'absolute', left: 0, top: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Box sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: OrderEventTypeColors[event.event_type] === 'success' ? '#22c55e'
                                                        : OrderEventTypeColors[event.event_type] === 'error' ? '#ef4444'
                                                        : OrderEventTypeColors[event.event_type] === 'warning' ? '#f59e0b'
                                                        : OrderEventTypeColors[event.event_type] === 'primary' ? '#3b82f6'
                                                        : OrderEventTypeColors[event.event_type] === 'info' ? '#06b6d4'
                                                        : '#94a3b8',
                                                }} />
                                                {idx < audit.length - 1 && (
                                                    <Box sx={{ width: 2, flexGrow: 1, bgcolor: '#e2e8f0', minHeight: 20, mt: 0.5 }} />
                                                )}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        label={OrderEventTypeLabels[event.event_type] || event.event_type}
                                                        size="small"
                                                        color={OrderEventTypeColors[event.event_type] || 'default'}
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', height: 22 }}
                                                    />
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                        {formatDateTime(event.timestamp || event.created_at)}
                                                    </Typography>
                                                </Box>
                                                {event.details && (
                                                    <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.8rem', mt: 0.5 }}>
                                                        {typeof event.details === 'string' ? event.details : JSON.stringify(event.details)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={() => setDetailDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // ==================== RENDER: BROKER CONNECTION DIALOG ====================
    const renderBrokerDialog = () => (
        <Dialog open={brokerDialog} onClose={() => setBrokerDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5 }}>
                {brokerEditing ? 'Edit Broker Connection' : 'Add Broker Connection'}
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Broker Name</InputLabel>
                            <Select
                                value={brokerForm.broker_name}
                                onChange={(e) => {
                                    const newBroker = e.target.value;
                                    setBrokerForm((prev) => {
                                        const updates = { ...prev, broker_name: newBroker };
                                        if (newBroker === 'ALPACA' && !brokerEditing) {
                                            updates.protocol_type = 'REST_API';
                                            updates.api_endpoint = 'https://paper-api.alpaca.markets/v2';
                                            updates.is_paper_trading = true;
                                            updates.connection_name = prev.connection_name || 'Alpaca Paper Trading';
                                        }
                                        return updates;
                                    });
                                }}
                                label="Broker Name"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(BrokerName).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{BrokerNameLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Connection Name"
                            value={brokerForm.connection_name}
                            onChange={(e) => setBrokerForm((prev) => ({ ...prev, connection_name: e.target.value }))}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Protocol Type</InputLabel>
                            <Select
                                value={brokerForm.protocol_type}
                                onChange={(e) => setBrokerForm((prev) => ({ ...prev, protocol_type: e.target.value }))}
                                label="Protocol Type"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(ProtocolType).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{ProtocolTypeLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="API Endpoint"
                            value={brokerForm.api_endpoint}
                            onChange={(e) => setBrokerForm((prev) => ({ ...prev, api_endpoint: e.target.value }))}
                            placeholder="https://api.broker.com/v1"
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={brokerForm.is_paper_trading}
                                    onChange={(e) => setBrokerForm((prev) => ({ ...prev, is_paper_trading: e.target.checked }))}
                                    sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontSize: '0.875rem' }}>Paper Trading (Simulated)</Typography>}
                        />
                    </Grid>
                    {brokerForm.broker_name === 'ALPACA' && (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Alpaca API Key"
                                    value={brokerForm.credentials_api_key}
                                    onChange={(e) => setBrokerForm((prev) => ({ ...prev, credentials_api_key: e.target.value }))}
                                    placeholder="PK..."
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Alpaca API Secret"
                                    type="password"
                                    value={brokerForm.credentials_api_secret}
                                    onChange={(e) => setBrokerForm((prev) => ({ ...prev, credentials_api_secret: e.target.value }))}
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={() => setBrokerDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveBrokerConnection}
                    variant="contained"
                    disabled={submitting || !brokerForm.broker_name || !brokerForm.connection_name || !brokerForm.protocol_type}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Saving...' : (brokerEditing ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: DELETE CONFIRMATION DIALOG ====================
    const renderDeleteDialog = () => (
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                Confirm Delete
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.875rem' }}>
                    Are you sure you want to delete the broker connection <strong>{deleteTarget?.connection_name}</strong>? This action cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button onClick={handleDeleteBrokerConnection} variant="contained" color="error" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: AMEND ORDER DIALOG ====================
    const renderAmendDialog = () => (
        <Dialog open={amendDialog} onClose={() => setAmendDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MuiIcons.EditNote sx={{ color: '#3b82f6' }} />
                Amend Order {amendTarget ? `#${amendTarget.id}` : ''}
                {amendTarget && (
                    <Chip
                        label={amendTarget.symbol}
                        size="small"
                        sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', ml: 'auto' }}
                    />
                )}
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {amendTarget && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                            {OrderSideLabels[amendTarget.order_side] || amendTarget.order_side} &bull; {TEEOrderTypeLabels[amendTarget.order_type] || amendTarget.order_type} &bull; {TEEAssetClassLabels[amendTarget.asset_class] || amendTarget.asset_class}
                        </Typography>
                    </Box>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Quantity"
                            type="number"
                            value={amendForm.quantity}
                            onChange={(e) => handleAmendFormChange('quantity', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    {amendTarget && NEEDS_LIMIT_PRICE.includes(amendTarget.order_type) && (
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Limit Price"
                                type="number"
                                value={amendForm.limit_price}
                                onChange={(e) => handleAmendFormChange('limit_price', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 0, step: '0.01' }}
                            />
                        </Grid>
                    )}
                    {amendTarget && NEEDS_STOP_PRICE.includes(amendTarget.order_type) && (
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Stop Price"
                                type="number"
                                value={amendForm.stop_price}
                                onChange={(e) => handleAmendFormChange('stop_price', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 0, step: '0.01' }}
                            />
                        </Grid>
                    )}
                    {amendTarget && NEEDS_TRAILING.includes(amendTarget.order_type) && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Trailing Amount ($)"
                                    type="number"
                                    value={amendForm.trailing_amount}
                                    onChange={(e) => handleAmendFormChange('trailing_amount', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Trailing Percent (%)"
                                    type="number"
                                    value={amendForm.trailing_percent}
                                    onChange={(e) => handleAmendFormChange('trailing_percent', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }}
                                    inputProps={{ min: 0, step: '0.1' }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={() => setAmendDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmitAmend}
                    variant="contained"
                    disabled={submitting || !amendForm.quantity}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                >
                    {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: CANCEL ORDER CONFIRMATION DIALOG ====================
    const renderCancelDialog = () => (
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                Cancel Order
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.875rem' }}>
                    Are you sure you want to cancel this order for <strong>{cancelTarget?.symbol}</strong> ({cancelTarget?.quantity} shares)? This will request cancellation from the broker.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setCancelDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Keep Order
                </Button>
                <Button onClick={handleCancelOrder} variant="contained" color="error" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Cancel Order
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view order management. Required permission: <strong>tr-tee-orders</strong>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <MuiIcons.Receipt sx={{ fontSize: 28, color: ACCENT }} />
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.4rem', lineHeight: 1.2 }}>
                        Order Management
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                        Create, monitor, and manage trade orders across broker connections
                    </Typography>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            minHeight: 48,
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                >
                    <Tab icon={<MuiIcons.ListAlt sx={{ fontSize: 18 }} />} iconPosition="start" label="Active Orders" />
                    <Tab icon={<MuiIcons.History sx={{ fontSize: 18 }} />} iconPosition="start" label="Order History" />
                    <Tab icon={<MuiIcons.Cable sx={{ fontSize: 18 }} />} iconPosition="start" label="Broker Connections" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderActiveOrders()}
            {activeTab === 1 && renderOrderHistory()}
            {activeTab === 2 && renderBrokerConnections()}

            {/* Dialogs */}
            {renderOrderDialog()}
            {renderDetailDialog()}
            {renderBrokerDialog()}
            {renderDeleteDialog()}
            {renderAmendDialog()}
            {renderCancelDialog()}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default OrderManagementPage;
